import { FC, ReactNode, useState } from 'react';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import { useIntl, defineMessages } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link';

// styles
import styles from './PdpSharedIconBubbles.scss';

import EmailIcon from 'dibs-icons/exports/Email';
import { PdpSharedSendToFriendModalLazy } from '../PdpSharedModals/PdpSharedSendToFriendModal/PdpSharedSendToFriendModalLazy';

// helpers
import { trackEmailClick } from '../tracking/iconBubblesTracking';

import { PdpSharedIconBubbleEmail_viewer$data } from './__generated__/PdpSharedIconBubbleEmail_viewer.graphql';
import { PdpSharedIconBubbleEmail_item$data } from './__generated__/PdpSharedIconBubbleEmail_item.graphql';
import { PdpSharedIconBubbleEmail_user$data } from './__generated__/PdpSharedIconBubbleEmail_user.graphql';

const messages = defineMessages({
    label: {
        id: 'PdpSharedIconBubbleEmail.label',
        defaultMessage: 'Share {title} by email',
    },
});

type State = {
    modalOpen: boolean;
    modalState: null | 'SUCCESS' | 'ERROR';
    isLoading: boolean;
    localSenderEmail: string;
    isRecentlySignedUpUser: boolean;
};

type Props = {
    viewer: PdpSharedIconBubbleEmail_viewer$data;
    item: PdpSharedIconBubbleEmail_item$data;
    user: PdpSharedIconBubbleEmail_user$data | null;
    skuId?: string | null;
    label?: ReactNode;
};

const updateState =
    (update: Partial<State>) =>
    (state: State): State => ({
        ...state,
        ...update,
    });

const PdpSharedIconBubbleEmailComponent: FC<Props> = ({ item, skuId, user, viewer, label }) => {
    const { formatMessage } = useIntl();
    const [state, setState] = useState<State>({
        modalOpen: false,
        modalState: null,
        isLoading: false,
        localSenderEmail: '',
        isRecentlySignedUpUser: false,
    });

    const { isPdpStfEnabled } = viewer;

    if (!isPdpStfEnabled) {
        return null;
    }

    const openModal = (): void => {
        setState(updateState({ modalOpen: true }));
    };

    const closeModal = (): void =>
        setState(
            updateState({
                modalOpen: false,
                modalState: null,
                isRecentlySignedUpUser: false,
            })
        );

    const setSuccess = (): void => setState(updateState({ modalState: 'SUCCESS' }));
    const setError = (): void => setState(updateState({ modalState: 'ERROR' }));

    const setIsLoading = (isLoading: boolean): void => setState(updateState({ isLoading }));

    const onClick = (): void => {
        openModal();
        trackEmailClick(item.serviceId);
    };

    const onUserLogin = (localSenderEmail: string, isRecentlySignedUpUser: boolean): void => {
        setState(
            updateState({
                localSenderEmail,
                isRecentlySignedUpUser,
            })
        );
        openModal();
    };

    const { modalOpen, modalState, localSenderEmail, isLoading, isRecentlySignedUpUser } = state;

    let imageSrc = item.firstPhotoWebPath;

    if (skuId) {
        const sku = (item.skus || []).find(s => s?.serviceId === skuId);
        const skuPhoto = sku?.firstPhotoWebPath;
        imageSrc = skuPhoto ? skuPhoto : imageSrc;
    }
    const senderEmail = user?.profile?.email ?? localSenderEmail;
    const isModalOpen = modalOpen || !!modalState;

    return (
        <Link
            className={label ? styles.bubbleWithLabel : styles.bubble}
            ariaLabel={formatMessage(messages.label, { title: item.title })}
            onClick={onClick}
            role="menuitem"
        >
            <EmailIcon className={styles.icon} />
            {label && <span>{label}</span>}
            <PdpSharedSendToFriendModalLazy
                key={senderEmail}
                type="PDP"
                modalOpen={isModalOpen}
                hasError={modalState === 'ERROR'}
                hasSuccess={modalState === 'SUCCESS'}
                setError={setError}
                setSuccess={setSuccess}
                senderEmail={senderEmail}
                imageSrc={imageSrc || ''}
                skuId={skuId ?? undefined}
                closeModal={closeModal}
                onUserLogin={onUserLogin}
                isRecentlySignedUpUser={isRecentlySignedUpUser}
                setIsLoading={setIsLoading}
                isLoading={isLoading}
                user={user}
                item={item}
                viewer={viewer}
                isSubmitInitiallyEnabled
            />
        </Link>
    );
};

export const PdpSharedIconBubbleEmail = createFragmentContainer(PdpSharedIconBubbleEmailComponent, {
    viewer: graphql`
        fragment PdpSharedIconBubbleEmail_viewer on Viewer {
            ... @include(if: $fetchPdp) {
                isPdpStfEnabled: featureFlag(feature: "enablePdpStf")
                ...PdpSharedSendToFriendModalLazy_viewer
            }
        }
    `,
    item: graphql`
        fragment PdpSharedIconBubbleEmail_item on Item {
            serviceId
            title
            firstPhotoWebPath(size: small)
            skus {
                serviceId
                firstPhotoWebPath(size: small)
            }
            ...PdpSharedSendToFriendModalLazy_item
        }
    `,
    user: graphql`
        fragment PdpSharedIconBubbleEmail_user on User {
            profile {
                email
            }
            ...PdpSharedSendToFriendModalLazy_user
        }
    `,
});
